/** HTML de impressão de receituário — dados do GET `/clinic/prescriptions/:id/print` (mesmo que a pré-visualização em Receituários). */
import { CLINIC_DOC_SIGNATURE_CSS } from './clinicPrintLetterhead';

const typeLabels: Record<string, string> = {
  simple: 'Simples',
  controlled_blue: 'Controlado (Azul)',
  controlled_yellow: 'Controlado (Amarelo)',
  special_white: 'Especial (Branco)',
  antimicrobial: 'Antimicrobiano',
  dental: 'Odontológico',
};

const typeColors: Record<string, string> = {
  simple: '#14b8a6',
  controlled_blue: '#3b82f6',
  controlled_yellow: '#f59e0b',
  special_white: '#8b5cf6',
  antimicrobial: '#ef4444',
  dental: '#06b6d4',
};

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDate(d: string | undefined | null): string {
  if (!d) return '-';
  const dt = new Date(String(d).split('T')[0] + 'T12:00:00');
  return dt.toLocaleDateString('pt-BR');
}

export function buildPrescriptionPrintHtml(printData: { prescription: any; clinic: any }): string {
  const printDataSafe = printData || { prescription: {}, clinic: {} };
  const pr = printDataSafe.prescription || {};
  const clinic = printDataSafe.clinic || {};

  const pt = pr.prescription_type || 'simple';
  const titleBlock =
    pt === 'controlled_blue'
      ? 'RECEITUÁRIO DE CONTROLE ESPECIAL'
      : pt === 'controlled_yellow'
        ? 'NOTIFICAÇÃO DE RECEITA A'
        : pt === 'special_white'
          ? 'NOTIFICAÇÃO DE RECEITA ESPECIAL'
          : pt === 'antimicrobial'
            ? 'RECEITUÁRIO - ANTIMICROBIANO'
            : 'RECEITUÁRIO';

  const items = Array.isArray(pr.items) ? pr.items : [];

  const medsHtml = items
    .map((item: any, idx: number) => {
      const namePart = `${idx + 1}) ${esc(item.medication_name)}${item.concentration ? ` ${esc(item.concentration)}` : ''}${item.pharmaceutical_form ? ` - ${esc(item.pharmaceutical_form)}` : ''}`;
      const controlled = item.is_controlled ? '<span class="controlled-badge">CONTROLADO</span>' : '';
      return `<div class="medication-item">
      <div class="medication-header">
        <div class="medication-name">${namePart} ${controlled}</div>
        ${item.quantity ? `<div class="medication-number">${esc(item.quantity)}</div>` : ''}
      </div>
      <div class="medication-details">
        <strong>Posologia:</strong> ${esc(item.dosage)}, ${esc(item.frequency)}
        ${item.route ? `<span> (via ${esc(item.route)})</span>` : ''}
        ${item.duration ? `<span> por ${esc(item.duration)}</span>` : ''}
      </div>
      ${item.instructions ? `<div class="medication-instructions"><strong>Instruções:</strong> ${esc(item.instructions)}</div>` : ''}
      ${item.special_instructions ? `<div class="medication-instructions"><strong>Atenção:</strong> ${esc(item.special_instructions)}</div>` : ''}
    </div>`;
    })
    .join('');

  const tc = typeColors[pt] || '#14b8a6';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receituário</title><style>
      @page { size: A4; margin: 15mm; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 0; }
      .print-container { max-width: 700px; margin: 0 auto; padding: 20px; }
      .doc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 3px solid #14b8a6; }
      .doc-head-logo { flex-shrink: 0; }
      .doc-head-logo img { display: block; max-width: 150px; max-height: 56px; }
      .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
      .doc-head-meta { flex-shrink: 0; max-width: 42%; font-size: 10px; color: #6b7280; text-align: right; line-height: 1.45; }
      .clinic-name { font-size: 18px; font-weight: 700; color: #0d9488; margin: 0; }
      .clinic-subtitle { font-size: 11px; color: #6b7280; margin-top: 2px; }
      .prescription-title { text-align: center; font-size: 16px; font-weight: 700; color: #0d9488; margin: 15px 0 5px; text-transform: uppercase; letter-spacing: 1px; }
      .prescription-type-badge { text-align: center; margin-bottom: 15px; }
      .prescription-type-badge span { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; }
      .prescription-number { text-align: center; font-size: 10px; color: #9ca3af; margin-bottom: 15px; }
      .section { margin-bottom: 12px; }
      .section-title { font-weight: 700; font-size: 12px; color: #0d9488; margin-bottom: 6px; padding-bottom: 3px; border-bottom: 1px solid #d1fae5; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
      .info-item { font-size: 11px; line-height: 1.4; }
      .info-label { font-weight: 600; color: #374151; }
      .info-value { color: #4b5563; }
      .medication-item { margin-bottom: 12px; padding: 10px; border: 1px solid #e5e7eb; border-radius: 6px; page-break-inside: avoid; }
      .medication-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
      .medication-name { font-weight: 700; font-size: 13px; color: #111; }
      .medication-number { font-size: 10px; color: #9ca3af; font-weight: 600; }
      .medication-details { font-size: 11px; color: #4b5563; line-height: 1.6; }
      .medication-instructions { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #e5e7eb; font-size: 10px; color: #6b7280; }
      .controlled-badge { display: inline-block; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; color: #fff; background: #ef4444; margin-left: 8px; }
      ${CLINIC_DOC_SIGNATURE_CSS}
      .validity { text-align: left; margin-top: 24px; margin-bottom: 0; font-size: 10px; color: #6b7280; }
      .footer-note { text-align: center; margin-top: 8px; font-size: 9px; color: #9ca3af; }
      .via-info { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px dashed #d1d5db; font-size: 9px; color: #9ca3af; font-style: italic; }
    </style></head><body><div class="print-container">
<div class="doc-head">
  ${clinic.logo_url ? `<div class="doc-head-logo"><img src="${esc(clinic.logo_url)}" alt="" /></div>` : '<div></div>'}
  <div class="doc-head-center">
    <div class="clinic-name">${esc(clinic.clinic_name || 'Clínica')}</div>
    ${clinic.clinic_subtitle ? `<div class="clinic-subtitle">${esc(clinic.clinic_subtitle)}</div>` : ''}
  </div>
  <div class="doc-head-meta">
    ${clinic.cnpj ? `<div>CNPJ: ${esc(clinic.cnpj)}</div>` : ''}
    ${clinic.address_street ? `<div>${esc(clinic.address_street)}${clinic.address_number ? `, ${esc(clinic.address_number)}` : ''}${clinic.address_neighborhood ? ` — ${esc(clinic.address_neighborhood)}` : ''}</div>` : ''}
    ${clinic.address_city || clinic.address_state ? `<div>${[clinic.address_city, clinic.address_state].filter(Boolean).join(' - ')}</div>` : ''}
    ${clinic.phone || clinic.whatsapp ? `<div>Tel: ${esc(clinic.phone || clinic.whatsapp)}</div>` : ''}
    ${clinic.email ? `<div>${esc(clinic.email)}</div>` : ''}
  </div>
</div>
<div class="prescription-title">${titleBlock}</div>
<div class="prescription-type-badge">
  <span style="background-color: ${tc}20; color: ${tc};">${esc(typeLabels[pt] || 'Simples')}</span>
</div>
<div class="prescription-number">
  Nº ${esc(pr.prescription_number)} | Emissão: ${formatDate(pr.issue_date)} | Validade: ${formatDate(pr.validity_date)}
  ${pr.via_count > 1 ? ` | ${pr.via_count}ª Via` : ''}
</div>
<div class="section">
  <div class="section-title">Dados do Paciente</div>
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Nome: </span><span class="info-value">${esc(pr.patient_name)}</span></div>
    ${pr.patient_cpf ? `<div class="info-item"><span class="info-label">CPF: </span><span class="info-value">${esc(pr.patient_cpf)}</span></div>` : ''}
    ${pr.patient_age_text ? `<div class="info-item"><span class="info-label">Idade: </span><span class="info-value">${esc(pr.patient_age_text)}</span></div>` : ''}
    ${pr.patient_address ? `<div class="info-item"><span class="info-label">Endereço: </span><span class="info-value">${esc(pr.patient_address)}</span></div>` : ''}
  </div>
</div>
${pr.diagnosis || pr.clinical_indication ? `<div class="section">
  <div class="section-title">Informações Clínicas</div>
  ${pr.diagnosis ? `<div class="info-item"><span class="info-label">Diagnóstico: </span><span class="info-value">${esc(pr.diagnosis)}${pr.diagnosis_code ? ` (CID: ${esc(pr.diagnosis_code)})` : ''}</span></div>` : ''}
  ${pr.clinical_indication ? `<div class="info-item"><span class="info-label">Indicação: </span><span class="info-value">${esc(pr.clinical_indication)}</span></div>` : ''}
</div>` : ''}
<div class="section">
  <div class="section-title">Prescrição</div>
  ${medsHtml || '<p style="font-size:11px;color:#9ca3af">(sem itens)</p>'}
</div>
${pr.notes ? `<div class="section"><div class="section-title">Observações</div><p style="font-size: 11px; line-height: 1.6;">${esc(pr.notes)}</p></div>` : ''}
${pr.is_continuous_use ? `<div style="text-align: center; margin: 12px 0; padding: 6px; background-color: #f0fdf4; border: 1px solid #d1fae5; border-radius: 4px; font-size: 11px; font-weight: 600; color: #0d9488;">USO CONTÍNUO</div>` : ''}
<div class="validity">Receituário válido até ${formatDate(pr.validity_date)}</div>
<div class="clinic-doc-sign-zone">
  <div class="clinic-doc-sign-row">
    <div class="clinic-doc-sign-slot">
      <div class="clinic-doc-sign-spacer"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">${esc(pr.professional_name || clinic.responsible_name || 'Profissional Responsável')}</p>
      ${pr.professional_registration || clinic.responsible_registration ? `<p class="clinic-doc-sign-caption">${esc(pr.professional_registration_type || clinic.responsible_registration_type || 'CRM')} ${esc(pr.professional_registration || clinic.responsible_registration)}${pr.professional_registration_state ? ` - ${esc(pr.professional_registration_state)}` : ''}</p>` : ''}
    </div>
    <div class="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
      <div class="clinic-doc-sign-spacer"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">Data: ___/___/______</p>
    </div>
  </div>
</div>
${pr.via_count > 1 ? `<div class="via-info">Este receituário deve ser emitido em ${esc(pr.via_count)} vias</div>` : ''}
${clinic.prescription_footer_text ? `<div class="footer-note">${esc(clinic.prescription_footer_text)}</div>` : ''}
</div></body></html>`;
}
